import { switchMap, filter, map, mapTo, distinctUntilChanged, startWith, exhaustMap, withLatestFrom, delay, take, skip, takeUntil } from 'rxjs/operators';
import { Subject, combineLatest, merge, BehaviorSubject, ReplaySubject, NEVER } from 'rxjs';
import Config from '../../configuration';
import { scriptLoader } from '../../utils';
import { PLAYBACK_PLAY, PLAYBACK_PLAYING, PLAYBACK_PAUSE, PLAYBACK_WAITING, PLAYBACK_END, PLAYBACK_STOPPED, PLAYER_ERROR, RENDERER_READY, PLAYBACK_SEEKED } from '../../store/types';
import { SEEK } from '../../core/command/types';
import { NEW_VIDEO, NEXT, USER_SEEK_START, VIDEO_START } from '../../types';
import { DAI_START, DAI_END } from '../../ad/dai/types';
import { EPG_CURRENT_CHANGED, EPG_CURRENT_START, TIMESHIFTING_MEDIAMETRIE_LIVE, TIMESHIFTING_MEDIAMETRIE_SHIFTING } from '../../core/timeshifting/types';
import { AD_NOT_REQUESTED, AD_REQUESTED, PAUSE_NOTIFICATION, PLAY_NOTIFICATION, STOP_NOTIFICATION } from './type';
import { MONITORING_EXEMPTED, MONITORING_OPTIN } from '../../core/monitoring/types';
import { Disposable } from '../../core';

const eStatNotification = {
  live: {
    [PLAYBACK_PLAY]: PLAY_NOTIFICATION,
    [PLAYBACK_PLAYING]: PLAY_NOTIFICATION,
    [PLAYBACK_PAUSE]: STOP_NOTIFICATION,
    [PLAYBACK_END]: STOP_NOTIFICATION,
    [PLAYBACK_STOPPED]: STOP_NOTIFICATION,
    [NEXT]: STOP_NOTIFICATION,
    [PLAYER_ERROR]: STOP_NOTIFICATION,
    [DAI_START]: PAUSE_NOTIFICATION,
    [DAI_END]: PLAY_NOTIFICATION,
    [EPG_CURRENT_CHANGED]: STOP_NOTIFICATION,
    [EPG_CURRENT_START]: PLAY_NOTIFICATION
  },
  replay: {
    [PLAYBACK_PLAY]: PLAY_NOTIFICATION,
    [PLAYBACK_PLAYING]: PLAY_NOTIFICATION,
    [PLAYBACK_PAUSE]: PAUSE_NOTIFICATION,
    [PLAYBACK_WAITING]: PAUSE_NOTIFICATION,
    [SEEK]: PAUSE_NOTIFICATION,
    [PLAYBACK_END]: STOP_NOTIFICATION,
    [PLAYBACK_STOPPED]: STOP_NOTIFICATION,
    [NEXT]: STOP_NOTIFICATION,
    [PLAYER_ERROR]: STOP_NOTIFICATION
  },
  timeshifting: {
    [PLAYBACK_PLAY]: PLAY_NOTIFICATION,
    [PLAYBACK_PLAYING]: PLAY_NOTIFICATION,
    [PLAYBACK_PAUSE]: PAUSE_NOTIFICATION,
    [PLAYBACK_WAITING]: PAUSE_NOTIFICATION,
    [SEEK]: PAUSE_NOTIFICATION,
    [PLAYBACK_END]: STOP_NOTIFICATION,
    [PLAYBACK_STOPPED]: STOP_NOTIFICATION,
    [NEXT]: STOP_NOTIFICATION,
    [PLAYER_ERROR]: STOP_NOTIFICATION,
    [TIMESHIFTING_MEDIAMETRIE_LIVE]: STOP_NOTIFICATION,
    [TIMESHIFTING_MEDIAMETRIE_SHIFTING]: STOP_NOTIFICATION,
    [EPG_CURRENT_CHANGED]: STOP_NOTIFICATION,
    [EPG_CURRENT_START]: PLAY_NOTIFICATION
  }
};

const allowedEvents = Object.keys(eStatNotification).reduce(
  (acc, key) => [...acc, Object.keys(eStatNotification[key])]
    .filter((v, i, a) => a.indexOf(v) === i),
  []
).flat();

export default class EStat extends Disposable {
  constructor(player) {
    super();
    this.mediasUpdated$ = new ReplaySubject(1);
    this.notifications$ = new Subject();
    this.errors$ = new Subject();
    this.tag$ = new ReplaySubject(1);

    const isPreviouslyLive$ = new BehaviorSubject(null);

    /* When we go over the threshold from live to timeshifting and vice versa,
       we need to know the previous state to send the stop event with the previous type.
       Indeed as the currentTime changes first, we would send the new value instead of the proper one.
       But this only needs to be applied when we change state, so we reset the stream to null after a short delay
     */
    player.events$.pipe(filter((evt) => evt === TIMESHIFTING_MEDIAMETRIE_LIVE), mapTo(false)).subscribe(isPreviouslyLive$);
    player.events$.pipe(filter((evt) => evt === TIMESHIFTING_MEDIAMETRIE_SHIFTING), mapTo(true)).subscribe(isPreviouslyLive$);
    player.events$.pipe(
      filter((evt) => [TIMESHIFTING_MEDIAMETRIE_SHIFTING, TIMESHIFTING_MEDIAMETRIE_LIVE].includes(evt)),
      delay(100), /* reset the stream to null after a short delay */
      mapTo(null)
    ).subscribe(isPreviouslyLive$);

    EStat.selectMediasStream(player)
      .subscribe(this.mediasUpdated$);

    const steps = [
      'setHeads',
      'setDomain',
      'setStreamingConfig',
      'setMediaInfos',
      'setLevels'
    ];

    EStat.configure({ mediasUpdated$: this.mediasUpdated$ })
      .pipe(withLatestFrom(player.freewheelController.isAdRequested$))
      .subscribe({
        next: ([{ EStatTag, medias }, isAdRequested]) => {
          const eStatConfig = EStat.prepareConfig(steps, medias, player, { isAdRequested });

          this.tag$.next(EStat.connect(EStatTag, eStatConfig));
        },
        error: (e) => this.errors$.next(e)
      });

    EStat.createEventNotificationStream({ player, mediasUpdated$: this.mediasUpdated$ })
      .subscribe(this.notifications$);

    EStat.mapTagMedia({
      player,
      tag$: this.tag$,
      notifications$: this.notifications$,
      isAdRequested$: player.freewheelController.isAdRequested$,
      mediasUpdated$: this.mediasUpdated$,
      isPreviouslyLive$,
      steps
    }).subscribe({
      next: ({ tag, notification }) => tag.notifyPlayer(notification),
      error: (e) => this.errors$.next(e)
    });
  }

  dispose() {
    this.tag$
      .pipe(take(1))
      .subscribe((tag) => tag.notifyPlayer(STOP_NOTIFICATION));
    super.dispose();
  }

  static configure({ mediasUpdated$ }) {
    return scriptLoader(`${document.location.protocol}//${Config.eStat.url}`).pipe(
      map(() => global.eStatTag),
      switchMap((EStatTag) => mediasUpdated$.pipe(map((medias) => ({ EStatTag, medias }))))
    );
  }

  static selectMediasStream(player) {
    return player.epgController.isEpgable$.pipe(
      switchMap((isEpgable) => (isEpgable
        ? EStat.createStreamFromEPG(player)
        : EStat.createStreamFromMedias(player)
      ))
    );
  }

  static createStreamFromMedias({ medias$, events$, rendererController: { duration$ } }) {
    return medias$.pipe(
      switchMap((media) => {
        /**
           * We wait for RENDERER_READY in case the media is a raw live url and not an id.
           * isLive and streamDuration are updated in the store by the renderer.
           */
        const ready$ = events$.pipe(filter((e) => e === RENDERER_READY));
        /** this is a rxjs trick to wait for both stream the first time,
            and then trigger only when one of them changes
        */
        return merge(
          combineLatest(ready$, duration$).pipe(take(1)),
          ready$.pipe(withLatestFrom(duration$)).pipe(skip(1))
        ).pipe(
          take(1),
          map(([, duration]) => {
            if (media.markers.estat.streamDuration) return media;

            // If no streamDuration is provided, we correct it with the media duration
            const corrected = { ...media };
            corrected.markers.estat.streamDuration = Math.floor(duration);
            return corrected;
          })
        );
      })
    );
  }

  static createStreamFromEPG({ epgController, medias$ }) {
    return epgController.currentMetadata$
      .pipe(
        withLatestFrom(medias$),
        map(([epg, medias]) => ({
          ...medias,
          meta: {
            ...medias.meta,
            title: epg.title,
            image_url: epg?.image_url,
            pre_title: epg?.pre_title,
            additional_title: epg?.additional_title,
            editorial_title: epg?.editorial_title
          },
          markers: epg?.markers
        }))
      );
  }

  static mapTagMedia({ tag$, notifications$, mediasUpdated$, player, steps, isPreviouslyLive$, isAdRequested$ }) {
    return combineLatest(tag$, notifications$, mediasUpdated$).pipe(
      withLatestFrom(isPreviouslyLive$, isAdRequested$),
      map(([[tag, notification, media], isPreviouslyLive, isAdRequested]) => {
        const mediaDiffMode = EStat.resolveMediaDiffMode(player, media.markers.estat, media.video, notification, isPreviouslyLive);
        media.markers.estat.mediaDiffMode = mediaDiffMode; // eslint-disable-line no-param-reassign

        /**
         * Here we manage the update when user changed program from the EPG
         * We need to update the media to then value from the EPG on the tracking
         * by calling the tag.set(newConfig)
         * We skip the update for the STOP because whe want to use the right date from the last program that the user watched
         */
        if (notification !== STOP_NOTIFICATION) {
          tag.set(EStat.prepareConfig(steps, media, player, { isAdRequested }));
        }
        return ({ tag, notification });
      })
    );
  }

  static eventsToNotification({ player, mediasUpdated$ }) {
    const notif$ = merge(
      /* Ensure first eStat notification is a play or pause */
      player.events$.pipe(
        filter((evt) => [PLAYBACK_PLAYING, PLAYBACK_PLAY, PLAYBACK_PAUSE].includes(evt)),
        exhaustMap((evt) => player.events$.pipe(startWith(evt))),
        filter((evt) => evt !== PLAYBACK_PAUSE) // pause is handled below
      ),
      // Send pause only when it is not a pause from a seek session
      player.events$.pipe(
        withLatestFrom(player.userEvents$),
        filter(([evt, { action }]) => action !== USER_SEEK_START && evt === PLAYBACK_PAUSE),
        mapTo(PLAYBACK_PAUSE)
      ),
      player.errors$.pipe(
        filter(({ error }) => error.fatal),
        mapTo(PLAYER_ERROR)
      ),
      /**
       * A WAITING event may follow a SEEK event if the video hasn't fully buffered.
       * This will cause a PAUSE notification on eStat which will never resolve if there
       * are no subsequent PLAYING events.
       * => in that case : check if videoTag is paused on SEEKED event and emit PLAYBACK_PLAY
       * to notify eStat that video playback has resumed.
       * => inconsistent event order on Safari :
       * delay emission in case SEEKED event happens before WAITING event
       */
      player.commandController.commands$.pipe(
        filter(({ type }) => type === SEEK),
        mapTo(SEEK)
      ),
      player.events$.pipe(
        filter((evt) => evt === PLAYBACK_SEEKED),
        withLatestFrom(player.activeVideoTag$, (_, videoEl) => videoEl.paused),
        filter((paused) => !paused),
        delay(250),
        mapTo(PLAYBACK_PLAY)
      ),
      player.events$.pipe(filter((e) => [DAI_START, DAI_END].includes(e)))
    ).pipe(
      filter((event) => allowedEvents.includes(event))
    );

    return player.freewheelController.isAd$.pipe(switchMap(({ isAd }) => (isAd ? NEVER : combineLatest(notif$, mediasUpdated$).pipe(
      filter(([event, { video }]) => Object.keys(eStatNotification[EStat.setDiffusion(video)]).includes(event)),
      map(([event, { video }]) => eStatNotification[EStat.setDiffusion(video)][event]),
      distinctUntilChanged(),
      takeUntil(player.events$.pipe(filter((e) => NEW_VIDEO === e)))
    ))));
  }

  static createEventNotificationStream = ({ player, mediasUpdated$ }) => player.events$.pipe(
    filter((e) => e === VIDEO_START),
    switchMap(() => EStat.eventsToNotification({ player, mediasUpdated$ }))
  );

  static connect(EStatTag, config) {
    const contentTag = new EStatTag(config);
    return contentTag;
  }

  static prepareConfig(steps, config, player, adInfos) {
    return steps.reduce((newConfig, key) => {
      const preConfig = EStat[key](config, player, adInfos);
      return { ...newConfig, ...preConfig };
    }, {});
  }

  static setHeads(media) {
    const {
      markers: { estat = {} } = {}
    } = media;
    // Override by the customized eStat serial if any
    const {
      config: {
        estat: { serial, consentType } = {
          serial: estat.serial,
          consentType: media?.config?.consent?.estat === MONITORING_OPTIN ? MONITORING_OPTIN : MONITORING_EXEMPTED
        }
      }
    } = media;
    return {
      serial,
      consentType,
      measure: 'streaming',
      crmID: estat.crmID,
      netMeasurement: estat.netMeasurement
    };
  }

  static setDomain(config) {
    const { markers: { estat = {} } = {} } = config;
    return {
      domain: estat.dom,
      implementation: (window.self !== window.top) ? 'frame' : undefined
    };
  }

  static setDiffusion({ is_live: isLive, is_DVR: isDVR, timeshiftable }) {
    if (isLive && !timeshiftable) return 'live';
    if (isDVR || timeshiftable) return 'timeshifting';
    return 'replay';
  }

  static setStreamingConfig(config, player) {
    const { video, meta, markers: { estat = {} } = {} } = config;
    const title = meta.title ? meta.title : '-';
    return {
      streaming: {
        diffusion: EStat.setDiffusion(video),
        playerName: 'player FTV Web',
        streamName: estat.streamName ? estat.streamName : title,
        streamUrl: video.url,
        streamGenre: estat.streamGenre,
        streamDuration: video.is_live ? 0 : estat.streamDuration,
        playerVersion: global.MAGNETO_VERSION,
        callbackPosition: video.is_live ? undefined : player.getCurrentTime.bind(player)
      }
    };
  }

  static setMediaInfos(config, player) {
    const { markers: { estat = {} }, video } = config;
    return {
      mediaInfo: {
        mediaContentId: estat.mediaContentId,
        mediaDiffMode: EStat.resolveMediaDiffMode(player, estat, video),
        mediaChannel: estat.mediaChannel
      }
    };
  }

  static resolveMediaDiffMode(player, { mediaDiffMode }, { timeshiftable }, notification, isPreviouslyLive) {
    let diffMode = mediaDiffMode;
    const timeDifference = player.getDuration() - player.getCurrentTime();

    if (timeshiftable) {
      if (player.getCurrentTime() !== 0 && timeDifference >= 60) {
        diffMode = 'TIMESHIFTING';
      } else if (player.getCurrentTime() === 0 || timeDifference <= 50) {
        diffMode = 'LIVE';
      }

      if (notification === STOP_NOTIFICATION && isPreviouslyLive !== null) {
        diffMode = isPreviouslyLive ? 'LIVE' : 'TIMESHIFTING';
      }
    }

    return diffMode;
  }

  static setNewLevel7(newLevel7, media) {
    const { config: { diffusion: { mode, position, length } = {} } = {} } = media;

    if (!newLevel7) {
      return null;
    }

    if (mode && mode.match(/tunnel/i) && position !== null && length !== null) {
      return `${newLevel7}_${position}_${length}`;
    }
    return newLevel7;
  }

  static setNewLevel11(isAdRequested) {
    return isAdRequested ? AD_REQUESTED : AD_NOT_REQUESTED;
  }

  static setLevels(config, _, { isAdRequested }) {
    const { markers: { estat = {} } = {} } = config;
    const group = {};
    group.levels = {
      level_1: estat.level1,
      level_2: estat.level2,
      level_3: estat.level3,
      level_4: estat.level4,
      level_5: estat.level5
    };

    /**
     *  please respect the order, the first item will sent ml1 and the last will send mlx when x is the size of the array
     *  */
    group.multiLevels = [
      estat.newLevel1,
      estat.newLevel2,
      estat.newLevel3,
      estat.newLevel4,
      estat.newLevel5,
      estat.newLevel6,
      EStat.setNewLevel7(estat.newLevel7, config),
      estat.newLevel8,
      estat.newLevel9,
      estat.newLevel10,
      EStat.setNewLevel11(isAdRequested),
      estat.newLevel12,
      estat.newLevel13,
      estat.newLevel14,
      estat.newLevel15
    ];

    return group;
  }
}
